import { useQueryStringState } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Autocomplete, Box, TextField } from '@mui/material';
import { memo, useMemo } from 'react';
import { DropDownOption } from '../drop-down';
import * as Styled from './styles';
import * as SX from './sx';
import type { SelectFilterByQueryParamsProps as Props } from './types';
import { getOptionLabel, getOptionValue, isEmptyValue } from './utils';

export const SelectFilterByQueryParams = memo(
  ({ className, options, paramName }: Props) => {
    const { params, setParams, removeParam } = useQueryStringState();

    const paramValue = params[paramName];

    const selectedValue = useMemo(() => {
      if (isEmptyValue(paramValue)) {
        return (
          options.find((option) => isEmptyValue(getOptionValue(option))) || null
        );
      }
      return (
        options.find((option) => getOptionValue(option) === paramValue) || null
      );
    }, [paramValue, options]);

    const handleFilterChange = (
      _: React.SyntheticEvent,
      newValue: DropDownOption | null
    ) => {
      if (!newValue) {
        removeParam(paramName);
        return;
      }

      const valueToSet = getOptionValue(newValue);
      if (isEmptyValue(valueToSet)) {
        removeParam(paramName);
      } else {
        setParams({ [paramName]: valueToSet });
      }
    };

    return (
      <Styled.Wrapper
        className={classnames('SelectFilterByQueryParams', className)}
        sx={SX.root}
      >
        <Box sx={SX.row}>
          <Autocomplete
            size="medium"
            fullWidth
            options={options}
            value={selectedValue}
            onChange={handleFilterChange}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => {
              if (!value) return false;
              return getOptionValue(option) === getOptionValue(value);
            }}
            renderInput={(textFieldParams) => (
              <TextField
                {...textFieldParams}
                variant="outlined"
                autoComplete="off"
              />
            )}
          />
        </Box>
      </Styled.Wrapper>
    );
  }
);

SelectFilterByQueryParams.displayName = 'SelectFilterByQueryParams';
